import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AddressComponent } from '../../../../shared/components/address/address.component';
import { SectionsService } from '../../../../services/sections.service';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { ListTableComponent } from '../../../../shared/components/list-table/list-table.component';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { LoggerService } from '../../../../shared/services/logger.service';
import {
  LeadDetailCommunicationModel,
  LeadDetailCommunicationUpdadeModel,
} from '../../../../interfaces/lead-detail.model';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { NzAriaLabelDirective } from '../../../../shared/directives/nz-aria-label.directive';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { UserService } from '../../../../services/user.service';
import { StorageService } from '../../../../services/storage.service';
import { DropdownSelectComponent } from '../../../../shared/components/dropdown-select/dropdown-select.component';
import { MfaService } from '../../../../services/mfa.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import {
  DialogConfig,
  DialogService,
} from '../../../../shared/services/dialog.service';
import {
  ChangesFreezeService,
  FREEZE_TYPE,
  ChangesFreezeState,
  UNFROZEN,
} from '../../../../services/changes-freeze.service';

@Component({
  selector: 'ppl-lead-detail-comm-methods',
  standalone: true,
  imports: [
    NgZorroModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AddressComponent,
    ListTableComponent,
    AuthorizeDirective,
    ButtonComponent,
    NzAriaLabelDirective,
    DropdownSelectComponent,
  ],
  templateUrl: './lead-detail-comm-methods.component.html',
  styleUrl: './lead-detail-comm-methods.component.css',
})
export class LeadDetailCommMethodsComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  @ViewChild('loginWarningDialog') loginWarningDialog!: TemplateRef<any>;
  @ViewChild('deleteWarningDialog') deleteWarningDialog!: TemplateRef<any>;
  @ViewChild('freezeConfirmDialog') freezeConfirmDialog!: TemplateRef<any>;

  freezeState: ChangesFreezeState = UNFROZEN;
  /** Compliance-only: shows the Freeze/Unfreeze button */
  canToggleFreeze = false;
  /** true when the active role may not edit or delete because the account is frozen */
  changesBlocked = false;

  @ViewChild('contactHistoryDialog') contactHistoryDialog!: TemplateRef<any>;
  contactHistoryData: any[] = [];
  contactHistoryCols = [
    { field: 'contactMethod', header: 'Contact Method', jsonpath: 'contactMethod', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'operation', header: 'Action Performed', jsonpath: 'operation', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'oldValue', header: 'Old Value', jsonpath: 'oldValue', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'newValue', header: 'New Value', jsonpath: 'newValue', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'modifiedByName', header: 'Last Modified By', jsonpath: 'modifiedByName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'auditCreatedDate', header: 'Event Date', jsonpath: 'auditCreatedDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  /** History is limited to the two roles that may review contact changes. */
  get isHistoryRole(): boolean {
    return this.isEnrollmentFlow() && this.userService.isInRole(['PPL Admin', 'Compliance']);
  }

  mode = 'read';
  filledInfo!: any;
  contactForm!: FormGroup;
  cols: any;
  gridData: any[] = [];
  communicationType: any;
  copyOfGridData: any = [];
  steps: any;
  entry = 'base';
  target!: LeadDetailCommunicationUpdadeModel;
  selectedLead = 0;
  actionButtons: TableRowActionsModel = new TableRowActions();
  approvedStatus: string = STATIC_DATA.APPROVED;
  deniedStatus: string = STATIC_DATA.DENIED;
  leadStatus = '';
  isSectionEditable: boolean = false;
  isMobileView = window.innerWidth <= 768;
  phoneNUmberError = false;
  createdContact: any[] = [];
  selectedCommValue: string | undefined | null = null;
  private originalCommValue: string | null = null;
  private dialogSvc = inject(DialogService);
  private readonly LOGIN_LINKED_TYPES = ['email', 'mobile', 'altPhone'];

  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode = '';
  @Input() readOnly = false;
  @Input() restrictReferralEdit = false;
  @Input() set sectionData(data: string) {
    this.steps = data;
  }

  @Output() leadUpdated = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService,
    private authUtility: AuthorizationUtility,
    private userService: UserService,
    private storageService: StorageService,
    private mfaService: MfaService,
    private errorAlertService: ErrorAlertService,
    private freezeService: ChangesFreezeService
  ) {
    super();
  }

  ngOnInit(): void {
    this.entry = this.userService.getLeadEntry() ?? '';
    this.communicationType =
      this.lookupService.getLookupValue('Communication_Type');
    this.isSectionEditable = this.readOnly || this.restrictReferralEdit ? false : this.authUtility.isSectionEditable(this.pageId, this.tabId, 'CommunicationMethods');

    this.selectedLead = JSON.parse(
      this.userService.getSelectedLead()!
    );
    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;

      if (this.isFinalizedStatus()) {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
      } else {
        this.actionButtons.edit = true;
        this.actionButtons.delete = true;
      }
    });
    this.canToggleFreeze = this.freezeService.canToggleFreeze() && this.isEnrollmentFlow();
    this.loadFreezeState();
    this.createFormGroup();
    this.getFromData();
    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      this.gridData = data?.responsedata?.communicationData?.contactMethods;
      this.copyAndTransformGridData(this.gridData);
      this.communicationType = this.communicationType.map((item: any) => {
        return {
          ...item,
          disabled: this.shoulddisableContactMethod(item),
        };
      });
    });
  }

  shoulddisableContactMethod = (option: any) => this.createdContact.includes(option.lkpValueCode);

  isFinalizedStatus(): boolean {
    return this.leadStatus === this.approvedStatus
      || (this.entry === STATIC_DATA.REFERRAL
        && (this.leadStatus === 'Review Complete' || this.leadStatus === STATIC_DATA.DENIED));
  }

  /** Add is hidden once the lead is finalized; Freeze is not, so the two are gated separately. */
  get canAddContactMethod(): boolean {
    return !this.isFinalizedStatus() && !this.readOnly;
  }

  /** Freeze and history both exist only on enrolled accounts, not the lead/referral flows. */
  private isEnrollmentFlow(): boolean {
    return [STATIC_DATA.PARTICIPANT_ENROLLMENT, STATIC_DATA.PROVIDER_ENROLLMENT].includes(this.entry);
  }

  private loadFreezeState(isAfterUpdate = false): void {
    if (!this.isEnrollmentFlow()) {
      return;
    }
    this.freezeService
      .getFreezeState(
        this.programCode,
        this.entry,
        this.selectedLead,
        FREEZE_TYPE.CONTACT_DETAILS,
        isAfterUpdate
      )
      .subscribe((state) => {
        this.freezeState = state;
        this.applyFreeze();
        this.cdr.detectChanges();
      });
  }

  /**
   * Greys out (rather than hides) Edit/Delete for the restricted roles. The buttons stay visible
   * so staff can see that the account is frozen instead of wondering where the controls went.
   */
  private applyFreeze(): void {
    this.changesBlocked = this.freezeService.isBlocked(this.freezeState);
    this.copyOfGridData = this.copyOfGridData.map((row: any) => ({
      ...row,
      disable: this.changesBlocked,
      disableDelete: this.changesBlocked,
    }));
  }

  openContactHistory(): void {
    this.leadDetailsService
      .getContactHistory(this.programCode, this.entry, this.selectedLead)
      .subscribe((data: any) => {
        this.contactHistoryData = (data?.responsedata ?? []).map((item: any) => ({
          ...item,
          //show the same label the section above uses; 'Global' has no lookup entry
          contactMethod:
            super.getLookupValue(this.communicationType, item.contactMethod) || item.contactMethod,
          //12-hour local time, matching the Forms history dialog
          auditCreatedDate: this.dateFormatter.formatDate(item.auditCreatedDate, 'MM/dd/yyyy, hh:mm a'),
        }));
        const dialogConfig: DialogConfig = {
          title: 'Contact Details History',
          size: 'large',
          position: 'top',
          showClose: true,
          showButtons: false,
        };
        this.dialogSvc.openDialog(this.contactHistoryDialog, dialogConfig, () => { }, () => { });
        this.cdr.detectChanges();
      });
  }

  toggleFreeze(): void {
    const freezing = !this.freezeState.frozen;
    const dialogConfig: DialogConfig = {
      title: 'Confirmation',
      size: 'medium',
      position: 'top',
      showClose: true,
      showButtons: true,
      isOkText: 'Yes',
      cancelText: 'No',
    };
    this.dialogSvc.openDialog(
      this.freezeConfirmDialog,
      dialogConfig,
      () => this.confirmFreeze(freezing),
      () => { }
    );
  }

  private confirmFreeze(frozen: boolean): void {
    this.freezeService
      .setFreeze(
        this.programCode,
        this.entry,
        this.selectedLead,
        FREEZE_TYPE.CONTACT_DETAILS,
        frozen
      )
      .subscribe(() => this.loadFreezeState(true));
  }

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['communication_data_contact_methods'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  limitDigits(event: any): void {
    const selectedValue = this.contactForm?.get('contactMethod')?.value;
    if (selectedValue == 'mobile' || selectedValue === 'altPhone') {
      let value = event.target.value.replace(/\D/g, '');
      if (value.length > 10) {
        value = value.slice(0, 10);

        event.target.value = value;
        this.contactForm.get(selectedValue)?.setValue(value);
      }
    }

  }

  doMfa(selectedData: any, selectedCommId: number, action: 'edit' | 'delete') {
    if (this.userService.showMfaPopup()) {
      this.mfaService.doMfa().subscribe(success => {
        if (success) {
          if (action === 'delete') {
            if (selectedData) {
              this.delete(selectedData);
            }
          } else {
            this.editContactDetails(selectedData, selectedCommId);
          }
        } else {
           this.errorAlertService.openModal('error', 'Error', 'MFA verification failed or cancelled.');
        }
      });
    } else {
      if (action === 'delete') {
        if (selectedData) {
          this.delete(selectedData);
        }
      } else {
        this.editContactDetails(selectedData, selectedCommId);
      }
    }
  }

  doMfaAdd() {
    if (this.userService.showMfaPopup()) {
      this.mfaService.doMfa().subscribe(success => {
        if (success) {
          this.add();
        } else {
           this.errorAlertService.openModal('error', 'Error', 'MFA verification failed or cancelled.');
        }
      });
    } else {
      this.add();
    }
  }

  createFormGroup() {
    this.contactForm = this.fb.group({
      contactMethod: [''],
      leadCommuniDetailsId: [''],
      id: [],
      value: [],
      preferred: [true],
    });
  }

  async getFromData() {
    //table column information
    try {
      if (this.entry) {
        const tempCols = await this.sectionService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_CONTACT_LIST_HEADERS,
          this.entry
        );

        this.cols = tempCols?.contactMethods?.columns;
      }
    } catch (e) {
      this.logger.debug('Error in getting table headers', e);
    }
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.contactForm.reset();
    this.phoneNUmberError = false;
    setTimeout(() => this.cdr.detectChanges(), 1000);
  }

  action(event: any) {
    console.log('acto', event);

    //the buttons are already disabled when frozen; this stops any other path into edit/delete
    if (this.changesBlocked) {
      return;
    }

    const selectedCommId = this.extractId(event?.data);

    const selectedData = this.gridData.find((row: any) => {

      if (
        [row.communiDetailsId, row.leadCommuniDetailsId, row.referralCommuniDetailsId, row.participantCommuniDetailsId].includes(selectedCommId)
      ) {
        return row;
      }
    });

    if (event && event.action === 'edit') {
      if (this.userService.showMfaPopup()) {
        this.doMfa(selectedData, selectedCommId, 'edit');
      } else {
        this.editContactDetails(selectedData, selectedCommId);
      }
    }

    if (event && event.action === 'delete') {
      if (this.userService.showMfaPopup()) {
        this.doMfa(selectedData, selectedCommId, 'delete');
      } else {
        if (selectedData) {
          this.delete(selectedData);
        }
      }
    }
  }

  editContactDetails(selectedData: any, selectedCommId: number) {
    this.mode = 'edit';

    if (selectedData) {
      this.contactForm.patchValue(selectedData);

      if ([STATIC_DATA.REFERRAL, STATIC_DATA.PARTICIPANT_ENROLLMENT, STATIC_DATA.PROVIDER_ENROLLMENT].includes(this.entry)) {
        this.contactForm.patchValue({
          leadCommuniDetailsId: selectedCommId,
          id: this.selectedLead,
        });
      }
    }
  }

  copyAndTransformGridData(gridData: any[]) {
    this.createdContact = [];
    if (gridData) {
      const copyGridData = JSON.parse(JSON.stringify(gridData));

      this.copyOfGridData = copyGridData.map((item: any) => ({
        ...item,
        preferred: item.preferred ? 'Yes' : 'No',
        contactMethod:
          super.getLookupValue(this.communicationType, item.contactMethod) ||
          item.contactMethod,
        modifiedDate: super.formatDateString(item.modifiedDate),
        leadCommuniDetailsId:
          item.leadCommuniDetailsId || item.referralCommuniDetailsId || item.communiDetailsId,
        disable: this.changesBlocked,
        disableDelete: this.changesBlocked,
      }
      ));
      copyGridData.forEach((item: any) => {
        this.createdContact.push(item.contactMethod);
      })

      this.copyOfGridData = [...this.copyOfGridData];
      if (this.isFinalizedStatus()) {
        this.actionButtons.edit = false;
        this.actionButtons.delete = false;
      } else {
        this.actionButtons.edit = true;
        this.actionButtons.delete = true;
      }
    } else {
      this.copyOfGridData = [];
      this.actionButtons.edit = false;
      this.actionButtons.delete = false;
    }

  }

  add() {
    this.mode = 'add';
    this.contactForm.reset();
    this.contactForm.controls['id'].setValue(this.selectedLead);
  }

  save() {
    const data = { ...this.contactForm.value };
    this.phoneNUmberError = false;
    try {
      this.validationErrors = performValidation(data, this.secData, 'Save', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType());
    } catch (e) {
      this.logger.debug('Error in validations', e);
    }

    if (data.contactMethod === 'mobile' || data.contactMethod === 'altPhone') {
      const phoneNumber = data.value;
      if (!/^\d{10}$/.test(phoneNumber)) {
        this.phoneNUmberError = true;
      }

      if (/^(.)\1{9}$/.test(phoneNumber)) {
        this.phoneNUmberError = true;
      }

      const unrealisticPatterns = [
        '1234567890', '9876543210', '0123456789', '1234567890'
      ];
      if (unrealisticPatterns.includes(phoneNumber)) {
        this.phoneNUmberError = true;
      }
      const tollFreePrefixes = ['800', '888', '877', '866', '855', '844', '833', '822'];
      const reservedPrefixes = ['911', '411', '611', '811', '555'];
      if (tollFreePrefixes.includes(phoneNumber.slice(0, 3)) || reservedPrefixes.includes(phoneNumber.slice(0, 3))) {
        this.phoneNUmberError = true;
      }

      const areaCode = phoneNumber.slice(0, 3);
      const centralOfficeCode = phoneNumber.slice(3, 6);
      if (areaCode[0] === '0' || areaCode[0] === '1' || centralOfficeCode[0] === '0' || centralOfficeCode[0] === '1') {
        this.phoneNUmberError = true;
      }
    }

    if (Object.keys(this.validationErrors).length === 0 && !this.phoneNUmberError) {
      this.target = this.preparePayload(data);
      this.leadDetailsService
        .createUpdateLeadCommunicationMethods(this.target, this.entry)
        .subscribe(
          {
            next: () => {
              this.logger.info('Contact method saved successfully');
              this.leadUpdated.emit(true);
              this.cancelEdit();
            },
            error: () => {
              this.leadUpdated.emit(true);
              this.cancelEdit();
            }
          }
        );
    }
  }


  delete(commMethod: any) {
    const id = this.extractId(commMethod);
    this.leadDetailsService
      .deleteLeadCommunicationMethod(id, this.entry, this.programCode, this.selectedLead, commMethod.contactMethod)
      .subscribe(() => {
        this.logger.info('Contact method deleted successfully');
        this.leadUpdated.emit(true);
        /* this.getLatestLeadData(); */
      });
  }

  preparePayload(
    source: LeadDetailCommunicationModel,

  ): LeadDetailCommunicationUpdadeModel {
    const communiDetailsId: number | null = source.leadCommuniDetailsId ?? null;
    const existingContactMethod = this.gridData?.find(
      (row) => {
        if (
          communiDetailsId && [row.communiDetailsId, row.leadCommuniDetailsId, row.referralCommuniDetailsId, row.participantCommuniDetailsId].includes(communiDetailsId)
        ) {
          return row;
        }
      }
    );
    return {
      id: this.selectedLead,
      programCode: this.programCode,
      communiDetailsId,
      referralId: this.storageService.getReferralConsumerId(),
      communicationMethod: {
        ...source,
        modifiedDate: existingContactMethod?.modifiedDate
      },
    };
  }


  extractId(commMethod: any) {
    let id = commMethod?.leadCommuniDetailsId;
    if (this.entry === STATIC_DATA.REFERRAL) {
      id = commMethod.referralCommuniDetailsId;
    }
    if (this.entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
      id = commMethod.participantCommuniDetailsId
    }
    if (this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
      id = commMethod.communiDetailsId
    }
    return id;
  }

}
